import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";

import { useDiscoContent } from "hooks/useDiscoContent";

import { BackButton } from "components/BackButton";
import { ImmersiveReaderButton } from "components/ImmersiveReaderButton/ImmersiveReaderButton";
import { InfoMessage } from "components/InfoMessage/InfoMessage";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Player } from "components/Player/Player";

import { Heading1, Heading2 } from "styles/elements/Headings";
import { Box, CssFlex } from "styles/helpers/layout";
import { StyledPlayerContainer } from "./EdifyTopicDetailsPage.styled";

type EdifyTopicDetailsPagePathParams = {
  productId: string;
  packageId: string;
  chapterId: string;
  contentUnitId: string;
};

export const EdifyTopicDetailsPage: React.FC = observer(() => {
  const { t } = useTranslation("homepage");
  const { productId, packageId, chapterId, contentUnitId } =
    useParams<EdifyTopicDetailsPagePathParams>();
  const { loading: isLoading, edifyTopicDetails, fetchEdifyTopicDetails } = useDiscoContent();

  useEffect(() => {
    if (packageId && chapterId && contentUnitId) {
      fetchEdifyTopicDetails(packageId, chapterId, contentUnitId);
    }
  }, [packageId, chapterId, contentUnitId, fetchEdifyTopicDetails]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!edifyTopicDetails || !packageId || !chapterId || !productId || !contentUnitId) {
    return (
      <InfoMessage
        dataCy="no-content-message"
        icon="fas fa-debug"
        title={t("edifyTopicDetails.noContentError.title")}
      >
        {t("edifyTopicDetails.noContentError.description", { packageId, chapterId, contentUnitId })}
      </InfoMessage>
    );
  }

  const isTheory = edifyTopicDetails.type === "THEORY";

  const backButtonRedirectURL = buildUrlWithPathParams(
    PRODUCT_PAGES_ROUTES.PRODUCT_EDIFY_SUBJECT_DETAILS,
    {
      productId,
      packageId,
      chapterId,
    },
  );

  return (
    <Box>
      <BackButton
        dataCy="return-to-edify-subject-button"
        label={t("edifyTopicDetails.button.back", "Back")}
        redirectUrl={backButtonRedirectURL}
      />
      <CssFlex data-ir-target="edify-topic-details" flexDirection="column" rowGap={1}>
        <Heading1 data-cy="edify-topic-title">
          <ImmersiveReaderButton
            targetIndex={0}
            targetSelector={'[data-ir-target="edify-topic-details"]'}
          />
          {edifyTopicDetails.title}
        </Heading1>

        {/* -- Render all content sets within the unit -- */}
        <CssFlex flexDirection="column" rowGap={2}>
          {edifyTopicDetails.contentSets.map((contentSet) => (
            <CssFlex key={contentSet.id} flexDirection="column" rowGap={2}>
              {edifyTopicDetails.contentSets.length > 1 && (
                <Heading2 data-cy="edify-content-set-title">{contentSet.title}</Heading2>
              )}
              <StyledPlayerContainer data-cy="edify-content-set-content">
                <Player
                  id={`player-${contentSet.id}`}
                  data={{ topics: [contentSet.content] }}
                  // Hide "Finish" button if content unit is of type theory
                  config={isTheory ? { superSubmitButton: null } : undefined}
                />
              </StyledPlayerContainer>
            </CssFlex>
          ))}
        </CssFlex>
      </CssFlex>
    </Box>
  );
});
